import React, {
  FunctionComponent, useEffect, useRef, useState,
} from 'react';
import {
  Flex, Text,
} from 'rebass/styled-components';
import styled from 'styled-components';
import theme from 'styled-theming';
import ImageHeader from '../../assets/banner/graphic-header.svg';
import { useAppState } from '../../hooks';
import { Button, ButtonLink } from '../v2';
import { ModalInner, ModalWrapper } from './modal-styled-components';

export interface FeatureAnnouncementModalProps {
  feature: string;
  header: string;
  subheader: string;
  body: React.ReactNode;
  documentationLink: string;
  closeBanner?: () => void;
}

const headerTextColor = theme('mode', {
  light: (props: any) => props.theme.colors.white,
  dark: (props: any) => props.theme.colors.white,
});

const textColor = theme('mode', {
  light: (props: any) => props.theme.colors.black,
  dark: (props: any) => props.theme.colors.white,
});

const modalBackgroundColor = theme('mode', {
  light: (props: any) => props.theme.colors.glitter,
  dark: (props: any) => props.theme.colors.modalDarkBackground,
});

const modalHeaderGradient = theme('mode', {
  light: (props: any) => props.theme.gradients.modalHeader,
  dark: (props: any) => props.theme.gradients.modalHeader,
});

const AnnouncementModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  color: ${textColor};
`;

const Header = styled.div`
  color: ${headerTextColor};
  padding: 36px;
  font-size: 32px;
  font-weight: 300;
  background: ${modalHeaderGradient};
  background-image: url(${ImageHeader}), ${modalHeaderGradient};
  border-top-left-radius: ${(props) => props.theme.radius.large};
  border-top-right-radius: ${(props) => props.theme.radius.large};
  background-repeat: no-repeat;
  background-position: center right;
`;

const Subheader = styled.div`
  font-size: 18px;
  line-height: 125%;
  font-weight: 400;
  color: ${(props) => props.theme.colors.greyLight};
  padding-top: 8px;
`;

const Section = styled(Flex)`
  overflow: auto;
  min-height: 383px;
  background: ${modalBackgroundColor};
`;

const Controls = styled(Flex)`
  background: ${modalBackgroundColor};
  border-radius: ${(props) => props.theme.radius.large};
  padding-top: 20px;
  padding-bottom: 36px;
`;

const DocumentationButton = styled((props) => <ButtonLink {...props} />)`
  padding: 8px 24px;
  margin-right: 16px;
`;

const FeatureAnnouncementModal: FunctionComponent<FeatureAnnouncementModalProps> = ({
  header, subheader, feature,
  documentationLink, body, closeBanner,
}: FeatureAnnouncementModalProps) => {
  const { features, isLoading } = useAppState();
  const [show, setShow] = useState<boolean>(false);
  const isMounted = useRef(true);

  async function onClose() {
    if (isMounted.current) {
      setShow(false);
    }
    // set flag in local storage
    localStorage.setItem(feature, 'true');
    if (closeBanner) {
      closeBanner();
    }
  }

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isLoading && features?.[feature]) {
      // TODO: Do we need to compare this (=== 'true')?
      const hasSeen = localStorage.getItem(feature);
      if (hasSeen) {
        setShow(false);
      } else {
        setShow(true);
      }
    } else {
      setShow(false);
    }
  }, [feature, features, isLoading]);

  return (show ? (
    <ModalWrapper data-testid={`feature-announcement-${feature}`}>
      <ModalInner p={0} onClick={(event) => event.stopPropagation()}>
        <AnnouncementModalContainer>
          <Header>
            <Text>{header}</Text>
            <Subheader>{subheader}</Subheader>
          </Header>
          <Section>
            {body}
          </Section>
          <Controls justifyContent="center">
            <DocumentationButton variant="secondary" link={documentationLink}>
              View documentation
            </DocumentationButton>
            <Button variant="primary" onClick={onClose}>Got it</Button>
          </Controls>
        </AnnouncementModalContainer>
      </ModalInner>
    </ModalWrapper>
  ) : null);
};

export default FeatureAnnouncementModal;
